import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Modal,
  Button,
  Row,
  Col,
  Form,
  FormControl,
  FormSelect,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import { BsDashLg, BsPlus, BsQuestionCircle } from "react-icons/bs";
import { useFormik } from "formik";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";
import { practiceRunSessionModalValidate } from "@/utils/validation";
import { constant, modal } from "@/utils/constant";
import { parseJwt } from "../../utils/custom";
import { reasonArray } from "@/utils/practiceRunConstant";

function PracticeRunsessionModal({
  hide,
  id = 0,
  show,
  handleAscending,
  isAscending,
  userData,
  sessionData,
  socket,
}) {
  const navigate = useNavigate();
  const { id: session_id } = useParams();
  const [organizationUser, setOrganizationUser] = useState([]);
  const [producerUser, setProducerUser] = useState([]);

  useEffect(() => {
    const fetchOrganizationUsersData = async () => {
      try {
        if(userData && organizationUser.length === 0){
          socket.emit('fetch-organization-users',{
            organization_id: userData?.organization?.id
          })
          socket.on("fetch-organization-users", (response) => {
            if(response.status === true){
              setOrganizationUser(response.data.organization_user);
              setProducerUser(response.data.producer_user);
            }
            socket.off("fetch-organization-users");
          });
        }
			} catch (error) {
			}
    };

    fetchOrganizationUsersData();
  }, [userData, organizationUser, socket]);

  const session = sessionData?.session || {};
  const [isEdit, setIsEdit] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (id === 1) {
      setIsEdit(true);
    }
  }, [id]);

  const formik = useFormik({
    initialValues: {
      name: id === 1 ? session?.name || "" : "",
      date: id === 1 ? new Date(session?.session_date) : new Date(),
      facilitator: id === 1 ? session?.facilitator?.id : "",
      co_facilitator: id === 1 && session?.co_facilitator 
        ? parseInt(session.co_facilitator.id)
       : "",
      count: id === 1 ? session?.no_of_participant : "4",
      reason: id === 1 ? String(session?.practice_run_reason ?? "") : "",
    },
    validationSchema: practiceRunSessionModalValidate,
    onSubmit: async (values) => {
      if (error) return;

      setIsEdit(true);
      const decodedJwt = parseJwt(localStorage.getItem("token"));
      const addSessionDts = {
        name: values.name.trim(),
        organization_id: userData?.organization.id,
        no_of_participant: values.count,
        session_date: moment(values.date).format("MM/DD/YYYY"),
        facilitator: parseInt(values.facilitator),
        co_facilitator: parseInt(values?.co_facilitator)
          ? parseInt(values?.co_facilitator)
          : null,
        practice_run_reason: Number(values.reason),
        user_id: decodedJwt?.sub,
      };
      try {
        if (id === 1) {
          addSessionDts.id = session?.id;
          socket.emit("updatepractice-run-session-data", addSessionDts);
          socket.on("updatepractice-run-session-data", (response) => {
            if (response.status === true) {
              if (!isAscending) {
                handleAscending(!isAscending);
              }
              hide();
            } else {
              setError(response.error);
              setIsEdit(false);
            }
            socket.off("updatepractice-run-session-data");
          });
        } else {
          socket.emit("create-practice-run-session", addSessionDts);
          socket.on("create-practice-run-session", (response) => {
            if (response.status === true) {
              if (!!session_id) {
                navigate("/session");
              } else if (!!isAscending) {
                handleAscending(!!isAscending);
              }
              hide();
            } else {
              setError(response.error);
              setIsEdit(false);
            }
            socket.off("create-practice-run-session");
          });
        }
      } catch (error) {
        setError(error.message);
        setIsEdit(false);
      }
    },
  });

  const increment = () => {
    setIsEdit(false);
    const count = parseInt(formik.values.count);
    if (count < 5) {
      formik.setFieldValue("count", (count + 1).toString());
      setError("");
    } else {
      setError("Maximum 5 participants allowed.");
    }
  };

  const decrement = () => {
    setIsEdit(false);
    const count = parseInt(formik.values.count);
    if (count > 4) {
      formik.setFieldValue("count", (count - 1).toString());
      setError("");
    } else {
      setError("Minimum 4 participants required.");
    }
  };

  const handleCountChange = (event) => {
    let newValue = parseInt(event.target.value);
    if (isNaN(newValue)) {
      newValue = 4;
    }
    if (newValue < 4) {
      newValue = 4;
      setError("Minimum 4 participants required.");
    } else if (newValue > 5) {
      newValue = 5;
      setError("Maximum 5 participants allowed.");
    } else {
      setError("");
    }
    formik.setFieldValue("count", newValue.toString());
  };

  const handleFacilitatorDisplay = () => {
    return organizationUser && organizationUser?.map((item, index) => {
      return (
        <option key={index} value={parseInt(item?.id)}>
          {item.first_name + " " + item.last_name}
        </option>
      );
    });
  };

  const handleProducerDisplay = () => {
    return (
      producerUser &&
      producerUser.map((item, index) => (
        <option key={index} value={parseInt(item?.id)}>
          {item.first_name + " " + item.last_name}
        </option>
      ))
    );
  };

  const handleReasonDisplay = () => {
    return reasonArray.map((item) => (
      <option key={item.id} value={String(item.id)}>
        {item.text}
      </option>
    ));
  };

  const renderTooltip = (props) => (
    <Tooltip className="tooltip-light" id="button-tooltip" {...props}>
      {constant?.reasonForPracticeTooltip}
    </Tooltip>
  );

  return (
    <>
      <Modal show={show} onHide={hide} size="xl" centered backdrop="static">
        <Modal.Header className="practice-run" closeButton>
          <Modal.Title>
            {id === 1 ? "Edit Practice Run Details" : "Schedule a Practice Run"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="pb-10 pt-10 px-lg-20">
          <p className="text-center mb-10">
            {id === 0
              ? modal?.sessionModalBodyText
              : modal?.sessionModalEditDetails}
          </p>
          <Form onSubmit={formik.handleSubmit}>
            <Row className="gx-lg-20 gy-6">
              <Col lg={6}>
                <div>
                  <label className="form-label">Session name</label>
                  <FormControl
                    onChange={(e) => {
                      setIsEdit(false);
                      formik.handleChange(e);
                    }}
                    value={formik.values.name}
                    disabled={id === 1 && session?.is_open_game_link}
                    name="name"
                    placeholder=""
                  />
                  {formik.submitCount > 0 && formik.errors.name && (
                    <div className="text-danger text-13">
                      {formik.errors.name}
                    </div>
                  )}
                </div>
                <div className="mt-6">
                  <label className="form-label">Session Date</label>
                  <div className="session-datepicker">
                    <DatePicker
                      name="date"
                      className="form-control"
                      selected={formik.values.date}
                      dateFormat="MMMM dd, yyyy"
                      minDate={new Date()}
                      disabled={true}
                    />
                  </div>
                </div>
                <div className="mt-6">
                  <label className="form-label">{constant?.facilitator}</label>
                  <FormSelect
                    name="facilitator"
                    value={formik.values.facilitator}
                    onChange={(e) => {
                      setIsEdit(false);
                      formik.handleChange(e);
                    }}
                    disabled={id === 1 && session?.is_open_game_link}
                  >
                    <option value="" disabled hidden>
                      {modal?.sessionModalSelectFacilitator}
                    </option>
                    {handleFacilitatorDisplay()}
                  </FormSelect>
                </div>
                {formik.submitCount > 0 && formik.errors.facilitator && (
                  <div className="text-danger text-13">
                    {formik.errors.facilitator}
                  </div>
                )}
                <div className="mt-6">
                  <label className="form-label">
                    {constant?.producer} (optional)
                  </label>
                  <FormSelect
                    name="co_facilitator"
                    value={formik.values.co_facilitator}
                    onChange={(e) => {
                      setIsEdit(false);
                      formik.handleChange(e);
                    }}
                    disabled={
                      ((id === 1 || id === 0) && session?.is_open_game_link) ||
                      (producerUser && producerUser.length === 0)
                    }
                  >
                    <option value="" disabled hidden>
                      {modal?.sessionModalSelectCoFacilitator}
                    </option>
                    {handleProducerDisplay()}
                  </FormSelect>
                </div>
              </Col>

              <Col lg={6}>
                <div>
                  <label className="form-label">
                    Number of participants (min 4, max 5)
                  </label>
                  <div className="app hstack gap-2">
                    <Button
                      variant="outline-grayish-blue"
                      className="px-3 py-2 text-primary rounded-2"
                      onClick={decrement}
                      disabled={
                        formik.values.count <= 4 || session?.is_open_game_link
                      }
                    >
                      <BsDashLg />
                    </Button>
                    <FormControl
                      value={formik.values.count}
                      onChange={(e) => {
                        setIsEdit(false);
                        handleCountChange(e);
                      }}
                      className="w-20 text-center"
                      name="count"
                      disabled={session?.is_open_game_link}
                    />
                    <Button
                      variant="outline-grayish-blue"
                      className="px-3 py-2 text-primary rounded-2"
                      onClick={increment}
                      disabled={
                        formik.values.count >= 5 || session?.is_open_game_link
                      }
                    >
                      <BsPlus />
                    </Button>
                  </div>
                  {error && <p className="text-danger text-13">{error}</p>}
                </div>

                <div className="mt-6">
                  <label className="form-label">
                    What is the reason for your practice?{" "}
                    <OverlayTrigger
                      placement="right"
                      overlay={renderTooltip}
                    >
                      <span className="text-primary" style={{ cursor: "pointer" }}>
                        <BsQuestionCircle />
                      </span>
                    </OverlayTrigger>
                  </label>
                  <FormSelect
                    name="reason"
                    value={formik.values.reason}
                    onChange={(e) => {
                      setIsEdit(false);
                      formik.handleChange(e);
                    }}
                    disabled={id === 1 && session?.is_open_game_link}
                  >
                    <option value="" disabled hidden>
                      Select one
                    </option>
                    {handleReasonDisplay()}
                  </FormSelect>
                  {formik.submitCount > 0 && formik.errors.reason && (
                    <div className="text-danger text-13">
                      {formik.errors.reason}
                    </div>
                  )}
                </div>
              </Col>

              <Col lg={12} className="mt-10 text-center">
                <Button
                  onClick={formik.handleSubmit}
                  size="lg"
                  className="practice-run-button font-semibold px-sm-20"
                  disabled={isEdit}
                >
                  {id === 1 ? "Save" : "Schedule Practice"}
                </Button>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
      </Modal>
    </>
  );
}
export default PracticeRunsessionModal;
